/**
 * BYO Flow core services.
 *
 * Thin, mockable async/pure wrappers that DELEGATE to existing code rather than
 * reinventing payloads:
 *  - source/sink config building reuses {@link createFlowService}.
 *  - code-container creation reuses the same payload shape as
 *    SaveCustomProcessor / SourceConfigureStep#getCustomProcessorId.
 *
 * Where a store dispatch is required we accept it as an argument instead of
 * importing the store, so shells stay in control and these helpers remain easy
 * to unit-test.
 */

import { Base64 } from 'js-base64';
import { cloneDeep, get, set, unset } from 'lodash';
import { TransformType } from 'new/components/ReusableTransform/types';
import createFlowService from 'new/components/flows/service/createFlowService';
import {
  BYTES_PER_GB,
  LEGACY_RUNTIME_VAR_KWARGS_PATH,
  parseExtraData,
  parsePackages,
  RUNTIME_VAR_KWARGS_PATH,
  serializePackages,
  type ByoCodeValue,
} from './model';

/** Minimal dispatch signature shells already have (`ThunkDispatch`-compatible). */
export type DispatchLike = (action: any) => Promise<any> | any;

/**
 * Code-container payload accepted by the backend code-container API. Matches
 * the object built inline in SaveCustomProcessor.tsx and
 * SourceConfigureStep#getCustomProcessorId so payloads do not drift.
 */
export interface CodeContainerPayload {
  name?: string;
  description?: string;
  resource_type: 'source_custom';
  output_type: TransformType;
  code_type: 'python';
  code_encoding: 'base64';
  /** Base64-encoded Python source. */
  code: string;
  code_config: {
    /** Comma-separated `name==version` requirements string (RayDryRunner.scala). */
    packages: string;
    extra_data: string;
  };
  reusable?: boolean;
}

export interface BuildCodeContainerOptions {
  name?: string;
  description?: string;
  /** When true, marks the container reusable (Save-as flow). Default false. */
  reusable?: boolean;
  /**
   * Ray worker resource config — merged into extra_data.runtime_config so
   * mergeWithExtraData in RayApiCodecsV2 picks it up without extra threading.
   */
  performanceConfig?: {
    cpu: number;
    memory: number;
    timeout: number;
    retries: number;
    concurrency: number;
  };
  /**
   * The extra_data already saved on the container being UPDATED in place.
   * When given, keys the UI doesn't model are preserved (see mergeExtraData).
   * Omit when creating a fresh container.
   */
  baseExtraData?: string;
}

/** Top-level extra_data keys the BYO editor owns outright (rewritten on every save). */
const UI_OWNED_TOP_LEVEL_KEYS = ['ui_runtime_var_secret_keys'];

/** Unset `path` in `obj` (mutating), then prune any parent objects the removal leaves empty. */
function unsetAndPruneEmptyParents(obj: Record<string, unknown>, path: readonly string[]): void {
  unset(obj, path);
  for (let depth = path.length - 1; depth > 0; depth -= 1) {
    const parentPath = path.slice(0, depth);
    const parent = get(obj, parentPath);
    if (!parent || typeof parent !== 'object' || Object.keys(parent).length) return;
    unset(obj, parentPath);
  }
}

/**
 * Remove the runtime-variable kwargs from a parsed extra_data object
 * (non-mutating on the input), pruning any parent objects the removal leaves
 * empty so a save with no variables doesn't leave `{"job_spec":{"payload":{}}}`
 * junk behind. Also removes the legacy `customCode.kwargs` (only the kwargs —
 * other `customCode` keys stay) so a migrated flow doesn't keep a stale copy.
 */
function withoutRuntimeVarKwargs(obj: Record<string, unknown>): Record<string, unknown> {
  const next = cloneDeep(obj);
  unsetAndPruneEmptyParents(next, RUNTIME_VAR_KWARGS_PATH);
  unsetAndPruneEmptyParents(next, LEGACY_RUNTIME_VAR_KWARGS_PATH);
  return next;
}

/**
 * Build the extra_data string to persist.
 *
 * - `extraData` is the UI-authored fragment (serializeRuntimeVars output).
 * - `perf`, when given, is written under `runtime_config`
 *   (RayApiCodecsV2.mergeWithExtraData deep-merges extraData at the top level
 *   of SubmitBatchJobRequest, so `runtime_config` lands directly in
 *   BatchJobRuntimeConfig).
 * - `baseExtraData`, when given, is the extra_data already saved on the
 *   container being UPDATED. The UI only models the runtime-variable kwargs
 *   (+ their secret-keys sidecar) and `runtime_config`, so everything else in
 *   it (unknown top-level keys, sibling keys under `job_spec`) is preserved,
 *   and its `runtime_config` is kept whenever `perf` is not overwriting it
 *   (e.g. UI_BYO_PERFORMANCE_CONFIG off). Without this, every edit-save
 *   rebuilt extra_data from the runtime variables alone and silently erased a
 *   previously-saved runtime_config and any other keys (NEX-22636). The
 *   kwargs themselves are always REPLACED, never merged, so a variable the
 *   user deleted really goes away.
 */
function mergeExtraData(
  extraData: string | undefined,
  perf: BuildCodeContainerOptions['performanceConfig'],
  baseExtraData?: string,
): string {
  if (baseExtraData === undefined && !perf) return extraData ?? '';
  // extraData is normally serializeRuntimeVars JSON, but older/legacy sources
  // can carry a non-JSON extra_data string — fall back to a fresh object
  // instead of throwing and losing the whole save.
  const ui = parseExtraData(extraData);
  let merged: Record<string, unknown> = {};
  if (baseExtraData !== undefined) {
    merged = withoutRuntimeVarKwargs(parseExtraData(baseExtraData));
    for (const key of UI_OWNED_TOP_LEVEL_KEYS) delete merged[key];
    const kwargs = get(ui, RUNTIME_VAR_KWARGS_PATH);
    if (kwargs !== undefined) set(merged, RUNTIME_VAR_KWARGS_PATH, kwargs);
    for (const [key, value] of Object.entries(ui)) {
      if (key !== RUNTIME_VAR_KWARGS_PATH[0]) merged[key] = value;
    }
  } else {
    merged = ui;
  }
  if (perf) {
    merged.runtime_config = {
      task_num_cpus: perf.cpu,
      task_memory_bytes: Math.round(perf.memory * BYTES_PER_GB),
      max_wait_seconds: perf.timeout,
      max_item_requeues: perf.retries,
      max_concurrency: perf.concurrency,
    };
  }
  return Object.keys(merged).length ? JSON.stringify(merged) : '';
}

/**
 * Build the code-container payload for a BYO code value.
 *
 * - `code_config.packages` is the serialized (comma-separated) requirements
 *   string built from `code.packages`.
 * - `code_config.extra_data` mirrors `code.extraData`, with performance config
 *   merged under `runtime_config` so the IMC codec picks it up automatically.
 * - `code` is base64-encoded Python source.
 *
 * Pure: no dispatch, no I/O.
 */
export function buildCodeContainerPayload(
  code: ByoCodeValue,
  options: BuildCodeContainerOptions = {},
): CodeContainerPayload {
  return {
    ...(options.name !== undefined ? { name: options.name } : {}),
    ...(options.description !== undefined ? { description: options.description } : {}),
    resource_type: 'source_custom',
    output_type: TransformType.CUSTOM,
    code_type: 'python',
    code_encoding: 'base64',
    code: Base64.encode(code.code ?? ''),
    code_config: {
      packages: serializePackages(code.packages ?? []),
      extra_data: mergeExtraData(code.extraData, options.performanceConfig, options.baseExtraData),
    },
    ...(options.reusable !== undefined ? { reusable: options.reusable } : {}),
  };
}

/**
 * Decode a stored code container's `code` + `code_config.packages` into the
 * shape a BYO editor/draft needs. Pure — no dispatch, no I/O.
 *
 * Shared by every BYO surface that lets a user pick "Use Existing Function":
 * a `source_custom`'s attached container (on edit hydration) and a general
 * reusable transform the user just picked (on both edit and CREATE — see
 * {@link ensureCodeContainerId}'s `mode: 'predefined'` handling for why the
 * picked processor's code always needs to be loaded, not just referenced).
 * Extracted here once other callers of this exact decode step reached two
 * (edit hydration + predefined-processor preview) rather than letting the
 * create path grow its own third, drifting copy.
 */
export function decodeCodeContainerContent(container: any): { code: string; packages: string[] } {
  if (!container) return { code: '', packages: [] };
  let code = '';
  try {
    code =
      container.code_encoding === 'base64'
        ? Base64.decode(container.code ?? '')
        : typeof container.code === 'string'
          ? container.code
          : JSON.stringify(container.code ?? '');
  } catch {
    code = '';
  }
  const rawPackages = container.code_config?.packages ?? '';
  const packages = parsePackages(String(rawPackages));
  return { code, packages };
}

/**
 * Resolve the code-container id for a BYO code value, creating OR updating
 * the container as appropriate.
 *
 * - When `deps.existingContainerId` is provided (editing a source that
 *   already has an attached container), dispatches
 *   `updateCodeContainerAction(id, payload)` (PUT) so the save updates the
 *   existing container in place instead of orphaning it and creating a new
 *   one on every edit — confirmed bug, fixed 2026-08-19.
 * - Otherwise dispatches `createCodeContainerAction(payload)` (POST) and
 *   returns the resulting id. Both actions are injected so this stays
 *   store-agnostic (shells pass `createCodeContainer`/`updateCodeContainer`
 *   from `js/actions/transform`).
 * - `mode === 'predefined'` with a real `predefinedId`: point the source
 *   straight at that container id — no create, no update, no copying its
 *   code anywhere. `LoadCustomProcessor.tsx` already scopes "Use Existing
 *   Function" to `resource_type === 'source_custom'` only, which is exactly
 *   what the backend's own `DataSource` validation requires a source's
 *   `code_container_id` to be (`{source, source_custom}`) — so every id this
 *   picker can produce is already valid to reference directly. An earlier
 *   version of this function predated that filter and instead copied the
 *   picked container's decoded code into a fresh/existing `source_custom`
 *   payload, on the theory that pointing at it directly could hit "Invalid
 *   code container resource type" for a non-source_custom pick. That's no
 *   longer possible given the filter, and the copy was actively harmful: it
 *   silently overwrote whichever container was previously attached with a
 *   COPY of the picked one's code, marked it non-reusable, and lost the
 *   "this source just references container X" relationship — so reopening
 *   the source read that other container's now-false `reusable` and fell
 *   back to "Provide Your Own Function", looking like the pick never took.
 *   Confirmed live 2026-08-21.
 * - `mode === 'predefined'` with `predefinedId == null` AND no code text
 *   means nothing has been chosen yet (canvas Step 1 creating the bare
 *   source, before the user reaches Step 2's code step) — returns `null`
 *   without creating a container. Regression found + fixed 2026-08-20: this
 *   guard was missing, so every canvas-created BYO source got a real,
 *   empty-code `source_custom` container (and was immediately activated)
 *   before the user ever wrote anything.
 */
export async function ensureCodeContainerId(
  code: ByoCodeValue,
  deps: {
    dispatch: DispatchLike;
    createCodeContainerAction: (payload: CodeContainerPayload) => any;
    updateCodeContainerAction?: (id: number | string, payload: CodeContainerPayload) => any;
    /** Existing container id to UPDATE instead of creating a new one. */
    existingContainerId?: number | string | null;
    options?: BuildCodeContainerOptions;
  },
): Promise<number | string | null> {
  if (code.mode === 'predefined' && code.predefinedId != null) {
    return code.predefinedId;
  }
  const nothingChosenYet =
    code.mode === 'predefined' && code.predefinedId == null && !code.code?.trim();
  if (nothingChosenYet && deps.existingContainerId == null) {
    return null;
  }
  const payload = buildCodeContainerPayload(code, deps.options);
  if (deps.existingContainerId != null && deps.updateCodeContainerAction) {
    const response = await deps.dispatch(
      deps.updateCodeContainerAction(deps.existingContainerId, payload),
    );
    // updateCodeContainer thunk resolves with `{ result }` too, but the id
    // never changes on an update — fall back to the id we already had.
    return response?.result ?? deps.existingContainerId;
  }
  const response = await deps.dispatch(deps.createCodeContainerAction(payload));
  // createCodeContainer thunk resolves with `{ result }` (the new id).
  return response?.result ?? null;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Activate a freshly-created BYO source and resolve its nexset (data_set) id.
 *
 * A freshly-created BYO source stays `INITIALIZED` and its nexset is never
 * detected until it's activated, so this always calls `activateSourceAction`
 * first (non-fatal — the poll below still retries even if activation itself
 * errors), then polls `loadDataSourceAction` up to 8×/1.5s apart for the
 * backend-detected nexset id.
 *
 * Extracted from the Studio shell (`useStudioDraft.ts`) so the canvas shell
 * uses the exact same activation path instead of the `addDetectedDataset`
 * shortcut it previously substituted — that shortcut stamped every source
 * with a fixed syslog mock schema regardless of the real connector type and
 * never activated the source at all. Confirmed bug, fixed 2026-08-19.
 */
export async function activateByoSourceAndResolveNexset(
  sourceId: number | string,
  deps: {
    dispatch: DispatchLike;
    activateSourceAction: (id: number | string) => any;
    loadDataSourceAction: (id: number) => any;
  },
): Promise<number | string | null> {
  try {
    await deps.dispatch(deps.activateSourceAction(sourceId));
  } catch {
    /* non-fatal — the nexset-id poll below still retries */
  }
  for (let attempt = 0; attempt < 8; attempt += 1) {
    try {
      const res: any = await deps.dispatch(deps.loadDataSourceAction(Number(sourceId)));
      const src = res?.entities?.sources?.[res?.result];
      const fromSrc = (src?.data_sets ?? [])[0];
      const dsId = (fromSrc && (fromSrc.id ?? fromSrc)) ?? null;
      if (dsId != null) return dsId;
    } catch {
      // A single transient poll failure (e.g. a dropped connection) used to
      // reject the whole function immediately, burning all 7 remaining
      // attempts — and since both call sites in useStudioDraft.ts invoke this
      // fire-and-forget (`void resolveNexsetId(...)`), that rejection became
      // an unhandled promise rejection with no user-visible error. Treat a
      // failed attempt the same as "not resolved yet" and keep retrying.
      // Confirmed bug, fixed 2026-08-20.
    }
    await sleep(1500);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Pass-through config builders.
//
// These delegate to createFlowService so the BYO core uses the exact same
// source/sink config construction as DirectFlow — no payload reinvention.
// ---------------------------------------------------------------------------

type FlowService = ReturnType<typeof createFlowService>;

// Lazily instantiate so importing this module (e.g. for the pure
// buildCodeContainerPayload helper) does not eagerly build the file/database
// service dependency tree.
let _flowService: FlowService | null = null;
function flowSvc(): FlowService {
  if (!_flowService) _flowService = createFlowService();
  return _flowService;
}

/** Build a default source config (delegates to createFlowService). */
export function buildSourceConfig(args: Parameters<FlowService['createDefaultSourceConfig']>[0]) {
  return flowSvc().createDefaultSourceConfig(args);
}
